import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Clock, FileWarning, RefreshCw, UserRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchMasterProfile, safeError } from '../data/candidateApi';

function formatDate(value) {
  if (!value) return 'Not available';
  try { return new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(new Date(value)); } catch (_) { return 'Not available'; }
}

function StatePanel({ profile, jobId }) {
  const state = profile?.state || 'MISSING';
  const common = 'jmp-panel p-6 border-t-4';
  const setupPath = (mode = '') => {
    const params = new URLSearchParams();
    if (mode) params.set('mode', mode);
    if (jobId) params.set('job_id', jobId);
    const query = params.toString();
    return `/master-profile/setup${query ? `?${query}` : ''}`;
  };
  if (state === 'CONFIRMED') {
    return (
      <section className={`${common} border-t-[#00A676]`} data-testid="master-profile-confirmed-state">
        <CheckCircle className="h-7 w-7 text-emerald-700" aria-hidden="true" />
        <h2 className="mt-4 font-serif-display text-3xl font-medium">Master Profile ready</h2>
        <p className="mt-3 text-sm text-[#5f6c64]" data-testid="master-profile-last-updated">Last updated: {formatDate(profile?.last_updated_at)}</p>
        <Link to={setupPath('update')} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="master-profile-update-link">Update Master Profile</Link>
      </section>
    );
  }
  if (state === 'EXTRACTING') {
    return (
      <section className={`${common} border-t-[#3B82F6]`} data-testid="master-profile-extracting-state">
        <Clock className="h-7 w-7 text-[#3B82F6]" aria-hidden="true" />
        <h2 className="mt-4 font-serif-display text-3xl font-medium">We’re preparing your Master Profile</h2>
        <p className="mt-3 text-sm text-[#5f6c64]">You can keep this page open or come back later.</p>
      </section>
    );
  }
  if (state === 'EXTRACTED_NOT_REVIEWED') {
    return (
      <section className={`${common} border-t-[#F2B84B]`} data-testid="master-profile-review-state">
        <UserRound className="h-7 w-7 text-[#F2B84B]" aria-hidden="true" />
        <h2 className="mt-4 font-serif-display text-3xl font-medium">Review your Master Profile</h2>
        <p className="mt-3 text-sm text-[#5f6c64]">Please review and confirm before we use it for fit analysis.</p>
        <Link to={setupPath('review')} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="master-profile-review-link">Review Master Profile</Link>
      </section>
    );
  }
  if (state === 'EXTRACTION_FAILED') {
    return (
      <section className={`${common} border-t-[#E45858]`} data-testid="master-profile-failed-state">
        <FileWarning className="h-7 w-7 text-[#E45858]" aria-hidden="true" />
        <h2 className="mt-4 font-serif-display text-3xl font-medium">Profile setup needs attention</h2>
        <p className="mt-3 text-sm text-[#5f6c64]">We could not prepare usable profile evidence from that upload. You can recover manually or try setup again.</p>
        <Link to={setupPath('recover')} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="master-profile-recovery-link">Recover manually</Link>
      </section>
    );
  }
  return (
    <section className={`${common} border-t-[#10231D]`} data-testid="master-profile-missing-state">
      <AlertCircle className="h-7 w-7 text-[#10231D]" aria-hidden="true" />
      <h2 className="mt-4 font-serif-display text-3xl font-medium">Set up your Master Profile</h2>
      <p className="mt-3 text-sm text-[#5f6c64]">Add profile evidence once, then reuse it for fit checks without re-uploading every time.</p>
      <Link to={setupPath()} className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="master-profile-setup-link">Start setup</Link>
    </section>
  );
}

export default function MasterProfilePage() {
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id') || '';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try { setProfile(await fetchMasterProfile()); } catch (err) { setError(safeError(err)); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  return (
    <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="master-profile-page">
      <div className="mx-auto max-w-5xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Stage 1</p>
        <h1 className="mt-3 font-serif-display text-4xl font-medium sm:text-5xl" data-testid="master-profile-title">Master Profile</h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#5f6c64]">Your reusable private profile for fit analysis and application documents.</p>
        <div className="mt-8">
          {loading && <div className="border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="master-profile-loading">Loading Master Profile…</div>}
          {error && <div className="border border-red-200 bg-red-50 p-6 text-sm text-red-700" data-testid="master-profile-error"><p>{error}</p><Button type="button" variant="outline" className="mt-4" onClick={load} data-testid="master-profile-retry-button"><RefreshCw className="mr-2 h-4 w-4" /> Retry</Button></div>}
          {!loading && !error && <StatePanel profile={profile} jobId={jobId} />}
        </div>
      </div>
    </main>
  );
}
